/**
 * QA-day checklist data. Mirrors /app/memory/SCHEDULED_TASKS.md but typed
 * for the React UI. Update SCHEDULED_TASKS.md AND this file together so
 * the source of truth (markdown) and the live page stay in sync.
 *
 * Each item has a stable `id` so localStorage tick state survives copy
 * edits to the `label`.
 */
export const QA_CHECKLIST = [
  {
    section: '1. Wording / copy review',
    blurb: "Pass through every public-facing surface and tighten the copy. Look for stale language, AI-slop verbosity, inconsistent tone, or text that doesn't reflect the current feature set.",
    groups: [
      {
        title: 'Home page (/home)',
        items: [
          { id: 'home.hero', label: 'Hero copy still reads "Predict tomorrow\'s balance" — confirm that\'s still the lead' },
          { id: 'home.roadmap', label: 'Roadmap section — status labels and ETAs are accurate' },
          { id: 'home.community', label: 'Community section copy reads cleanly' },
          { id: 'home.pricing', label: 'Stripe pricing pills (£4 / £7 / £40 / £70) match the live PACKAGES config' },
        ],
      },
      {
        title: 'FAQ / Help page (/help)',
        items: [
          { id: 'help.healthcheck', label: 'Add a section explaining the Account Health Check (once feature flag is flipped on)' },
          { id: 'help.password', label: 'Add a section about password reset / HIBP breach checks' },
          { id: 'help.stripe', label: 'Stripe checkout / refund / downgrade copy matches current behaviour' },
          { id: 'help.tiers', label: 'Section on "How tier upgrades work" — auto-comp, expiration, downgrade' },
        ],
      },
      {
        title: 'About page (/about)',
        items: [
          { id: 'about.founder', label: 'Founder story still feels right' },
          { id: 'about.socials', label: 'Social links work (X @_ForgeLedger, Facebook)' },
          { id: 'about.contact', label: 'Owner email / contact path that isn\'t admin@financeapp.com' },
          { id: 'about.origin', label: 'Write the Our Beginnings origin story (new section, currently empty)' },
        ],
      },
      {
        title: 'Pricing page (/pricing)',
        items: [
          { id: 'pricing.bullets', label: 'Bullet copy under each tier reflects the current feature matrix' },
          { id: 'pricing.damascus', label: 'Damascus bullets mention "Weekly Account Health Check+" (held-back, gate on flag)' },
          { id: 'pricing.founders', label: '"Founding offer" coupon copy still relevant' },
        ],
      },
    ],
  },
  {
    section: '2. Admin / Owner functional QA',
    blurb: 'Log in as Owner (SeanKeogh@forgeledger.net via OWNER_EMAIL) and verify every admin function works end-to-end. Test in incognito to confirm the role auto-promotion picks up on first request.',
    groups: [
      {
        title: 'Owner role',
        items: [
          { id: 'owner.pill', label: 'OWNER pill shows in the navbar (gold)' },
          { id: 'owner.access', label: 'Owner sees Staff Tools bar with all 6 links on /home' },
          { id: 'owner.change-password', label: 'Change password link works → rotation persists across logout/login' },
          { id: 'owner.forgot-password', label: '/forgot-password → email → /reset-password recovers the account' },
        ],
      },
      {
        title: 'CMS / Editorial',
        items: [
          { id: 'cms.help', label: 'Edit Help & FAQ — change a tip, save, see it on /help' },
          { id: 'cms.about', label: 'Edit About us — change mission paragraph, save, see it on /about' },
          { id: 'cms.pricing', label: 'Edit Pricing bullets — change a Steel bullet, save, see it on /pricing' },
          { id: 'cms.site-settings', label: 'Edit home tagline / intros — save, see on /home' },
          { id: 'cms.history', label: 'Page history shows past versions, restore works' },
        ],
      },
      {
        title: 'Moderation + community',
        items: [
          { id: 'mod.queue', label: 'Moderation queue shows pending suggestions; reject/approve flows work' },
          { id: 'roadmap.crud', label: 'Roadmap admin: create, edit status, change category — all save' },
        ],
      },
      {
        title: 'Tiers / billing',
        items: [
          { id: 'tiers.upgrade', label: 'Manage user tiers: comp a user to Steel, expire date works' },
          { id: 'tiers.metrics', label: '/admin/metrics dashboard renders, Exclude-test-data toggle filters MRR correctly' },
          { id: 'tiers.vat', label: '/admin/vat shows the 12-month trailing revenue + last warning/trigger state' },
        ],
      },
      {
        title: 'Digests + flags',
        items: [
          { id: 'flags.healthcheck', label: '/admin/feature-flags: toggle Account Health Check on/off' },
          { id: 'digest.preview', label: 'Trigger a digest preview email to yourself — verify formatting in inbox' },
        ],
      },
    ],
  },
];
